import {
  addSurfaceModeAtom,
  DEFAULT_ADD_SURFACE_MODE,
  meshPickTargetsAtom,
  pointCloudTargetsAtom,
} from '@/atoms/viewer.atom';
import { pushCommandAtom } from '@/atoms/snapping.atom';
import { pickSurface } from '@/components/3D/pickSurface';
import {
  applyHandleDrag,
  projectRayOntoLine,
  rectFromPolygonCoords,
  rectFromThreeCorners,
  rectFromVerticalCorners,
  rectToPolygonCoords,
  type SurfaceHandle,
  type SurfaceRect,
} from '@/components/3D/surfaceGeometry';
import { useSnapResolver } from '@/components/3D/useSnapResolver';
import { FeaturesQuery } from '@/graphql/graphql';
import type { SnapFeature } from '@/lib/snapping/candidates';
import { Line } from '@react-three/drei';
import { useThree, type ThreeEvent } from '@react-three/fiber';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as THREE from 'three';

const SURFACE_COLOR = '#3b82f6';

type HandleStyleVariant = { idle: string; hover: string; active: string };

const STYLES: Record<'corner' | 'edge' | 'center' | 'normal', HandleStyleVariant> = {
  corner: { idle: '#3b82f6', hover: '#60a5fa', active: '#1d4ed8' },
  edge: { idle: '#06b6d4', hover: '#22d3ee', active: '#0e7490' },
  center: { idle: '#f59e0b', hover: '#fbbf24', active: '#b45309' },
  normal: { idle: '#a855f7', hover: '#c084fc', active: '#7e22ce' },
};

const handleCategory = (h: SurfaceHandle): keyof typeof STYLES => {
  if (h.startsWith('corner-')) return 'corner';
  if (h.startsWith('edge-')) return 'edge';
  if (h === 'center') return 'center';
  return 'normal';
};

const baseSize = (h: SurfaceHandle): number => {
  if (h === 'normal') return 0.3;
  if (h.startsWith('corner-')) return 0.22;
  if (h === 'center') return 0.28;
  return 0.18;
};

const colorFor = (h: SurfaceHandle, hovered: boolean, active: boolean) => {
  const v = STYLES[handleCategory(h)];
  return active ? v.active : hovered ? v.hover : v.idle;
};

const sizeFor = (h: SurfaceHandle, hovered: boolean, active: boolean) => {
  const s = baseSize(h);
  if (active) return s * 1.3;
  if (hovered) return s * 1.15;
  return s;
};

const HANDLES_LIST: SurfaceHandle[] = [
  'corner-A',
  'corner-B',
  'corner-C',
  'corner-D',
  'edge-AB',
  'edge-BC',
  'edge-CD',
  'edge-DA',
  'center',
  'normal',
];

const rectCenter = (rect: SurfaceRect): THREE.Vector3 =>
  rect.A.clone().add(rect.B).add(rect.C).add(rect.D).multiplyScalar(0.25);

// Distance the depth handle sticks out from the rectangle along its normal.
const normalHandleOffset = (rect: SurfaceRect): number => Math.max(rect.C.clone().sub(rect.A).length() * 0.1, 0.3);

const cornerOf = (rect: SurfaceRect, h: SurfaceHandle): THREE.Vector3 | null => {
  switch (h) {
    case 'corner-A':
      return rect.A;
    case 'corner-B':
      return rect.B;
    case 'corner-C':
      return rect.C;
    case 'corner-D':
      return rect.D;
    case 'edge-AB':
      return rect.A.clone().add(rect.B).multiplyScalar(0.5);
    case 'edge-BC':
      return rect.B.clone().add(rect.C).multiplyScalar(0.5);
    case 'edge-CD':
      return rect.C.clone().add(rect.D).multiplyScalar(0.5);
    case 'edge-DA':
      return rect.D.clone().add(rect.A).multiplyScalar(0.5);
    case 'center':
      return rectCenter(rect);
    case 'normal':
      return rectCenter(rect).addScaledVector(rect.normal, normalHandleOffset(rect));
  }
  return null;
};

const cornersToTriple = (corners: Array<[number, number, number]>): SurfaceRect | null => {
  if (corners.length < 4) return null;
  return rectFromPolygonCoords([...corners, corners[0]] as [number, number, number][]);
};

interface AddSurfaceToolProps {
  features?: FeaturesQuery['featuresByLayer'][number][];
}

export const AddSurfaceTool = ({ features = [] }: AddSurfaceToolProps) => {
  const [mode, setMode] = useAtom(addSurfaceModeAtom);
  const pushCommand = useSetAtom(pushCommandAtom);
  const pcoTargets = useAtomValue(pointCloudTargetsAtom);
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const camera = useThree((state) => state.camera);
  const renderer = useThree((state) => state.gl);
  const size = useThree((state) => state.size);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const { t } = useTranslation('components');

  const getVisibleFeatures = useCallback<() => SnapFeature[]>(
    () => features.map((f) => ({ id: f.id, geometry: f.geometry as SnapFeature['geometry'] })),
    [features],
  );
  const resolve = useSnapResolver(getVisibleFeatures);

  const sizeRef = useRef(size);
  sizeRef.current = size;
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;

  const [hoverPoint, setHoverPoint] = useState<THREE.Vector3 | null>(null);
  const [hoveredHandle, setHoveredHandle] = useState<SurfaceHandle | null>(null);
  const [activeHandle, setActiveHandle] = useState<SurfaceHandle | null>(null);
  const draggingRef = useRef<{ handle: SurfaceHandle; plane: THREE.Plane } | null>(null);
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  const enabled = mode.enabled;
  const phase = mode.phase;
  const isPicking = phase === 'picking-1' || phase === 'picking-2' || phase === 'picking-3' || phase === 'picking-n';
  const isAdjusting = phase === 'adjusting';

  const draftRect = useMemo(() => {
    if (!isAdjusting || !mode.draftCorners) return null;
    return cornersToTriple(mode.draftCorners);
  }, [isAdjusting, mode.draftCorners]);

  // Imperative geometry construction — rebuilt every time the rect changes so the
  // drag-translated rectangle actually moves on screen. (Using bufferGeometry's
  // onUpdate prop only fires once at mount, which was the root cause of "center drag
  // doesn't move the surface".)
  const fillGeometry = useMemo(() => {
    if (!draftRect) return null;
    const g = new THREE.BufferGeometry();
    const { A, B, C, D } = draftRect;
    g.setAttribute(
      'position',
      new THREE.BufferAttribute(
        new Float32Array([A.x, A.y, A.z, B.x, B.y, B.z, C.x, C.y, C.z, A.x, A.y, A.z, C.x, C.y, C.z, D.x, D.y, D.z]),
        3,
      ),
    );
    g.computeVertexNormals();
    return g;
  }, [draftRect]);

  const outlinePoints = useMemo(() => {
    if (!draftRect) return null;
    return [draftRect.A, draftRect.B, draftRect.C, draftRect.D, draftRect.A];
  }, [draftRect]);

  const pickedLinePoints = useMemo(() => {
    if (mode.pickedPoints.length < 2) return null;
    return mode.pickedPoints.map((p) => new THREE.Vector3(...p));
  }, [mode.pickedPoints]);

  // Reset cursor when tool is disabled.
  useEffect(() => {
    if (!enabled) {
      renderer.domElement.style.cursor = 'default';
      setHoverPoint(null);
    }
  }, [enabled, renderer]);

  // Hover preview during picking.
  useEffect(() => {
    if (!enabled || !isPicking) return;
    const dom = renderer.domElement;

    const onMove = (e: MouseEvent) => {
      if (draggingRef.current) return;
      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);
      // Surface hit is optional: the resolver can still snap to vector
      // geometry in screen space with no point cloud / mesh present. Clear the
      // preview only when there's neither a surface nor a snap target.
      const cursorPx: [number, number] = [
        ((x + 1) / 2) * sizeRef.current.width,
        ((1 - y) / 2) * sizeRef.current.height,
      ];
      const snap = resolveRef.current(
        cursorPx,
        hit ? [hit.position.x, hit.position.y, hit.position.z] : null,
      );
      if (snap.kind !== 'none') {
        setHoverPoint(new THREE.Vector3(...snap.position));
        dom.style.cursor = 'crosshair';
      } else {
        setHoverPoint(null);
        dom.style.cursor = 'default';
      }
    };

    dom.addEventListener('pointermove', onMove);
    return () => {
      dom.removeEventListener('pointermove', onMove);
      dom.style.cursor = 'default';
    };
  }, [enabled, isPicking, pcoTargets, meshTargets, camera, renderer, raycaster]);

  // Click-to-pick handler.
  useEffect(() => {
    if (!enabled || !isPicking) return;
    const dom = renderer.domElement;
    const DRAG_THRESHOLD = 5;

    const onPointerDown = (e: MouseEvent) => {
      pointerDownPos.current = { x: e.clientX, y: e.clientY };
    };
    const onPointerUp = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!pointerDownPos.current) return;
      const dx = e.clientX - pointerDownPos.current.x;
      const dy = e.clientY - pointerDownPos.current.y;
      pointerDownPos.current = null;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) return;
      if (draggingRef.current) return;

      const rect = dom.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      const hit = pickSurface(renderer, camera, raycaster, pcoTargets, meshTargets);

      // No surface hit is fine — try a screen-space snap to existing geometry.
      // Bail only when there's nothing to place on (no surface, no snap).
      const cursorPx: [number, number] = [
        ((x + 1) / 2) * sizeRef.current.width,
        ((1 - y) / 2) * sizeRef.current.height,
      ];
      const snap = resolveRef.current(
        cursorPx,
        hit ? [hit.position.x, hit.position.y, hit.position.z] : null,
      );
      if (snap.kind === 'none') return;
      const next = [...mode.pickedPoints, snap.position] as Array<[number, number, number]>;

      // Polygon mode: every click just appends a vertex and parks us in
      // 'picking-n' (or earlier picking phases for the first two clicks).
      // The user explicitly hits Finalize on the toolbar to commit. No
      // auto-rectangle build, no horizontal/vertical lock semantics.
      if (mode.polygonMode) {
        const nextPhase: typeof mode.phase =
          next.length === 1 ? 'picking-2' : next.length === 2 ? 'picking-3' : 'picking-n';
        setMode({ ...mode, pickedPoints: next, phase: nextPhase });
        setHoverPoint(null);
        return;
      }

      if (next.length < 3) {
        const nextPhase = next.length === 1 ? 'picking-2' : 'picking-3';
        setMode({ ...mode, pickedPoints: next, phase: nextPhase });
        setHoverPoint(null);
        return;
      }

      // Third pick — try to build a rectangle.
      // verticalLock: vertical wall (AB horizontal, v-axis = world up, height from
      //   click 3's Z). Click 3's XY is ignored. Implies horizontalLock.
      // horizontalLock (default): AB horizontal; plane can still tilt around AB.
      // Neither: free 3-point plane.
      const p1Vec = new THREE.Vector3(...next[0]);
      const p2Raw = new THREE.Vector3(...next[1]);
      const p3Vec = new THREE.Vector3(...next[2]);
      let built;
      if (mode.verticalLock) {
        built = rectFromVerticalCorners(p1Vec, p2Raw, p3Vec);
      } else {
        const p2Vec = mode.horizontalLock ? new THREE.Vector3(p2Raw.x, p2Raw.y, p1Vec.z) : p2Raw;
        built = rectFromThreeCorners(p1Vec, p2Vec, p3Vec);
      }
      if (!built) {
        toast.error(t('addSurfaceTool.collinearPicks'));
        // keep first two picks, drop the third — let user re-pick the third
        setMode({ ...mode, pickedPoints: next.slice(0, 2), phase: 'picking-3' });
        return;
      }
      const corners = rectToPolygonCoords(built);
      setMode({
        ...mode,
        pickedPoints: next,
        phase: 'adjusting',
        // store only the 4 unique corners (drop the closing duplicate)
        draftCorners: corners.slice(0, 4) as Array<[number, number, number]>,
      });
      setHoverPoint(null);
    };

    dom.addEventListener('pointerdown', onPointerDown);
    dom.addEventListener('pointerup', onPointerUp);
    return () => {
      dom.removeEventListener('pointerdown', onPointerDown);
      dom.removeEventListener('pointerup', onPointerUp);
    };
  }, [enabled, isPicking, mode, setMode, pcoTargets, meshTargets, camera, renderer, raycaster, t]);

  // Cancel via right-click during picking.
  useEffect(() => {
    if (!enabled || !isPicking) return;
    const dom = renderer.domElement;
    const onCtx = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMode({ ...DEFAULT_ADD_SURFACE_MODE });
    };
    dom.addEventListener('contextmenu', onCtx);
    return () => dom.removeEventListener('contextmenu', onCtx);
  }, [enabled, isPicking, renderer, setMode]);

  // Drag handler for handles. In-plane handles project the mouse ray onto the
  // surface's own plane; the depth handle projects onto the normal line through
  // the rect's center so the surface translates along its normal.
  const startHandleDrag = (e: ThreeEvent<PointerEvent>, handle: SurfaceHandle) => {
    if (!draftRect) return;
    e.stopPropagation();

    const isCornerHandle = handle.startsWith('corner-');
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(draftRect.normal, draftRect.origin);
    draggingRef.current = { handle, plane };
    setActiveHandle(handle);
    renderer.domElement.style.cursor = 'grabbing';

    const isNormalHandle = handle === 'normal';
    const lineAnchor = isNormalHandle ? rectCenter(draftRect) : null;
    const lineDir = isNormalHandle ? draftRect.normal.clone() : null;
    // For the normal (depth) handle: the handle visual sits at
    // `center + normalHandleOffset(rect) * normal` — NOT at the rect centre.
    // `applyHandleDrag` for the normal case treats its `newWorldPos` argument
    // as "the point that the centre should move to along the normal line",
    // so passing the raw mouse projection makes the rect snap such that its
    // new centre lands at the handle's old world position the moment the
    // user clicks (the surface jumps ~30 cm towards the camera). We capture
    // the initial signed distance along the normal line and subtract it on
    // every move so the first frame produces a zero offset and the rect
    // tracks the cursor's *delta* from there.
    let initialNormalT: number | null = null;

    // Capture corners before drag for undo history (corners only).
    const beforeCorners = isCornerHandle && mode.draftCorners
      ? (mode.draftCorners.slice() as Array<[number, number, number]>)
      : null;
    let lastCorners: Array<[number, number, number]> | null = null;

    const dom = renderer.domElement;

    const onMove = (ev: PointerEvent) => {
      ev.stopPropagation();
      const rect = dom.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

      let hitPoint: THREE.Vector3 | null;
      if (isNormalHandle && lineAnchor && lineDir) {
        hitPoint = projectRayOntoLine(raycaster.ray.origin, raycaster.ray.direction, lineAnchor, lineDir);
      } else {
        hitPoint = new THREE.Vector3();
        if (!raycaster.ray.intersectPlane(plane, hitPoint)) hitPoint = null;
      }
      if (!hitPoint) return;

      // Normal-handle delta correction: convert the absolute hit on the
      // normal line into a delta-from-pointerdown, then synthesise a
      // `lineAnchor + delta * lineDir` point so the existing
      // `applyHandleDrag` math (which expects "where should the centre go
      // along the normal axis") yields the right offset without modifying
      // the geometry helper.
      let effectiveHitPoint = hitPoint;
      if (isNormalHandle && lineAnchor && lineDir) {
        const t = hitPoint.clone().sub(lineAnchor).dot(lineDir);
        if (initialNormalT === null) initialNormalT = t;
        const dt = t - initialNormalT;
        effectiveHitPoint = lineAnchor.clone().addScaledVector(lineDir, dt);
      }

      // For corner handles, snap the plane-intersection world position through
      // the snap resolver before feeding it into applyHandleDrag.
      if (isCornerHandle) {
        const cursorPx: [number, number] = [
          ((x + 1) / 2) * sizeRef.current.width,
          ((1 - y) / 2) * sizeRef.current.height,
        ];
        const snap = resolveRef.current(
          cursorPx,
          [effectiveHitPoint.x, effectiveHitPoint.y, effectiveHitPoint.z],
        );
        effectiveHitPoint = new THREE.Vector3(...snap.position);
      }

      const updated = applyHandleDrag(draftRect, handle, effectiveHitPoint);
      if (!updated) return;
      const corners = rectToPolygonCoords(updated).slice(0, 4) as Array<[number, number, number]>;
      lastCorners = corners;
      setMode((prev) => ({ ...prev, draftCorners: corners }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp);
      draggingRef.current = null;
      setActiveHandle(null);
      renderer.domElement.style.cursor = hoveredHandle ? 'grab' : 'default';

      // Push undo command for corner drags when we have a clean before/after.
      if (isCornerHandle && beforeCorners && lastCorners) {
        const afterCorners = lastCorners;
        pushCommand({
          label: 'move surface corner',
          apply: () => setMode((prev) => ({ ...prev, draftCorners: afterCorners })),
          invert: () => setMode((prev) => ({ ...prev, draftCorners: beforeCorners })),
        });
      }
    };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp);
  };

  if (!enabled) return null;

  return (
    <>
      {/* Picking-phase visuals only — hide as soon as we enter the adjust phase
          so the original 3 click markers don't sit on top of the rectangle. */}
      {isPicking && (
        <>
          {mode.pickedPoints.map((p, i) => (
            <mesh key={`picked-${i}`} position={p} renderOrder={Number.MAX_SAFE_INTEGER}>
              <sphereGeometry args={[0.18, 12, 12]} />
              <meshBasicMaterial color={SURFACE_COLOR} transparent opacity={0.85} depthTest={false} />
            </mesh>
          ))}
          {hoverPoint && (
            <mesh position={hoverPoint.toArray()} renderOrder={Number.MAX_SAFE_INTEGER}>
              <sphereGeometry args={[0.15, 12, 12]} />
              <meshBasicMaterial color={SURFACE_COLOR} transparent opacity={0.4} depthTest={false} />
            </mesh>
          )}
          {pickedLinePoints && (
            <Line
              points={pickedLinePoints}
              color={SURFACE_COLOR}
              lineWidth={2}
              transparent
              opacity={0.7}
              depthTest={false}
            />
          )}
        </>
      )}

      {/* Adjusting phase — rectangle + handles */}
      {isAdjusting && draftRect && (
        <>
          {/* Filled rectangle (two triangles) */}
          {fillGeometry && (
            <mesh geometry={fillGeometry} renderOrder={Number.MAX_SAFE_INTEGER - 200}>
              <meshBasicMaterial
                color={SURFACE_COLOR}
                transparent
                opacity={0.25}
                side={THREE.DoubleSide}
                depthTest={false}
              />
            </mesh>
          )}

          {/* Edge outline */}
          {outlinePoints && (
            <Line
              points={outlinePoints}
              color={SURFACE_COLOR}
              lineWidth={2.5}
              transparent
              opacity={0.95}
              depthTest={false}
            />
          )}

          {/* Connector line from center to depth handle */}
          <Line
            points={[rectCenter(draftRect), cornerOf(draftRect, 'normal')!]}
            color={colorFor('normal', hoveredHandle === 'normal', activeHandle === 'normal')}
            lineWidth={2}
            transparent
            opacity={0.85}
            depthTest={false}
          />

          {/* Handles */}
          {HANDLES_LIST.map((handle) => {
            const pos = cornerOf(draftRect, handle);
            if (!pos) return null;
            const hovered = hoveredHandle === handle;
            const active = activeHandle === handle;
            return (
              <mesh
                key={handle}
                position={pos.toArray()}
                onPointerDown={(e) => startHandleDrag(e, handle)}
                onPointerOver={(e) => {
                  e.stopPropagation();
                  setHoveredHandle(handle);
                  if (!activeHandle) renderer.domElement.style.cursor = 'grab';
                }}
                onPointerOut={(e) => {
                  e.stopPropagation();
                  setHoveredHandle((prev) => (prev === handle ? null : prev));
                  if (!activeHandle) renderer.domElement.style.cursor = 'default';
                }}
                renderOrder={Number.MAX_SAFE_INTEGER}>
                <sphereGeometry args={[sizeFor(handle, hovered, active), 16, 16]} />
                <meshBasicMaterial
                  color={colorFor(handle, hovered, active)}
                  transparent
                  opacity={0.95}
                  depthTest={false}
                />
              </mesh>
            );
          })}
        </>
      )}
    </>
  );
};
